import { Box, Card, Checkbox, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { CurrencyData } from '@tix-ticketing/ui-components';
import { EventTicketsAccordion, ImageBox, TixlyIconButton } from '@tix-ticketing/ui-components';
import { CrossIcon } from '@tix-ticketing/ui-icons/outline';
import { pinToMinFontSize } from '@tix-ticketing/ui-theme';
import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { EventListItemDto, SubscriptionTypeGroupDto, TicketDto } from 'api/generated/types.gen';
import { SimpleIconButton } from 'components/common/buttons/simple-icon-button';
import { CartAsideEventCardBody } from 'components/common/cards/cart-aside-event-card-body';
import { EventCardBody } from 'components/common/cards/event-card-body';
import { StickyCardHeader, stickyCardSx } from 'components/common/cart/sticky-card-header';
import { byDisplayType, displayTypeMessages } from 'components/common/subscription/subscription-display-type-messages';
import { FormattedPrice } from 'lib/helpers/formatted-price';
import { ticketKey } from 'lib/helpers/pay-selection-helpers';
import type { PaySelection } from 'lib/hooks/use-pay-selection';
interface Props {
  subscription: SubscriptionTypeGroupDto;
  /** All events in the order, used to look up event metadata by id. */
  events: EventListItemDto[];
  currencyData: CurrencyData;
  showCustomerInfo?: boolean;
  inAside?: boolean;
  /**
   * Keeps the header pinned while the card's events and tickets scroll past
   * it: under the navbar on the page, or at the top of the mini cart's
   * scrolling box when `inAside`.
   */
  stickyHeader?: boolean;
  /** Replaces the display-type label under the subscription name. */
  subtitle?: ReactNode;
  /**
   * Full-width band between the subscription header and its events, for
   * anything the buyer has to be told about the subscription as a whole.
   */
  notice?: ReactNode;
  /** When omitted (e.g. on the receipt) the card renders read-only. */
  onRemove?: () => void;
  /**
   * Removes one seat from the subscription. Offered only where a removal is
   * settled in one go at checkout — a subscription's seats are a set the
   * backend keeps consistent, so deleting one outright can take the rest with
   * it.
   */
  onRemoveTicket?: (ticket: TicketDto) => void;
  onShow360Ticket?: (ticket: TicketDto) => void;
  onViewSeats?: (event: EventListItemDto, tickets: TicketDto[]) => void;
  /**
   * Live while the cart is running a select-items-to-pay selection. Renders a
   * checkbox per event and per ticket, or, when only whole items may move, a
   * single one for the whole subscription; omitted the card has none.
   */
  selection?: PaySelection;
}
export const CartSubscriptionCard = ({
  subscription,
  events,
  currencyData,
  showCustomerInfo = false,
  inAside = false,
  stickyHeader = false,
  subtitle,
  notice,
  onRemove,
  onRemoveTicket,
  selection,
  onShow360Ticket,
  onViewSeats
}: Props) => {
  const intl = useIntl();
  const ticketSelectLabel = intl.formatMessage({
    id: "cart.selectTicket",
    defaultMessage: [{
      "type": 0,
      "value": "Select ticket"
    }]
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const eventTicketGroups = subscription.eventTicketGroups ?? [];
  const subscriptionEvents = eventTicketGroups.map(group => ({
    group,
    event: events.find(e => e.id === group.eventId)
  })).filter((entry): entry is {
    group: typeof entry.group;
    event: EventListItemDto;
  } => entry.event !== undefined);
  const isSelecting = !!selection?.isSelecting;
  // When only whole items may move, the subscription is one item across all
  // its events, so its header carries the only checkbox on the card.
  const selectsWhole = isSelecting && !!selection?.wholeItemsOnly;
  const subscriptionTickets = eventTicketGroups.flatMap(group => group.tickets ?? []);
  const subscriptionCheckbox = selectsWhole && selection && <Checkbox checked={selection.ticketsState(subscriptionTickets) === 'all'} indeterminate={selection.ticketsState(subscriptionTickets) === 'some'} onChange={() => selection.toggleTickets(subscriptionTickets)} slotProps={{
    input: {
      'aria-label': intl.formatMessage({
        id: "cart.selectEventItems",
        defaultMessage: [{
          "type": 0,
          "value": "Select all tickets for "
        }, {
          "type": 1,
          "value": "event"
        }]
      }, {
        event: subscription.name ?? ''
      })
    }
  }} sx={{
    flexShrink: 0
  }} />;
  const removeButton = !onRemove ? null : inAside ? <TixlyIconButton color="monochrome" variant="text" size="small" onClick={onRemove} aria-label={intl.formatMessage({
    id: "common.remove",
    defaultMessage: [{
      "type": 0,
      "value": "Remove"
    }]
  })} sx={{
    position: 'absolute',
    top: 8,
    right: 8,
    color: 'action.active'
  }}>
      <CrossIcon />
    </TixlyIconButton> : <SimpleIconButton onClick={onRemove} ariaLabel={intl.formatMessage({
    id: "common.remove",
    defaultMessage: [{
      "type": 0,
      "value": "Remove"
    }]
  })} icon={<CrossIcon />} sx={isMobile ? {
    position: 'absolute',
    top: 11,
    right: 11,
    color: 'action.active'
  } : {
    alignSelf: 'center'
  }} />;
  const metaRow = <Stack direction="row" spacing={1.5} sx={{
    alignItems: 'center'
  }} divider={<Box aria-hidden sx={theme => ({
    width: 4,
    height: 4,
    borderRadius: '50%',
    bgcolor: theme.palette.text.disabled
  })} />}>
      <Typography variant={isMobile ? 'body2Medium' : 'body1Medium'} component="span">
        <FormattedMessage id="event.xEvents" defaultMessage={[{
        "type": 6,
        "value": "count",
        "options": {
          "=0": {
            "value": [{
              "type": 0,
              "value": "0 events"
            }]
          },
          "=1": {
            "value": [{
              "type": 0,
              "value": "1 event"
            }]
          },
          "other": {
            "value": [{
              "type": 7
            }, {
              "type": 0,
              "value": " events"
            }]
          }
        },
        "offset": 0,
        "pluralType": "cardinal"
      }]} values={{
        count: subscription.eventCount ?? 0
      }} />
      </Typography>
      <Typography variant={isMobile ? 'body2Medium' : 'body1Medium'} component="span">
        <FormattedMessage id="cart.subscription.ticketsTotal" defaultMessage={[{
        "type": 6,
        "value": "count",
        "options": {
          "one": {
            "value": [{
              "type": 7
            }, {
              "type": 0,
              "value": " ticket total"
            }]
          },
          "other": {
            "value": [{
              "type": 7
            }, {
              "type": 0,
              "value": " tickets total"
            }]
          }
        },
        "offset": 0,
        "pluralType": "cardinal"
      }]} values={{
        count: subscription.ticketCount ?? 0
      }} />
      </Typography>
    </Stack>;
  return <Card variant="outlined" sx={{
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    ...(stickyHeader && stickyCardSx)
  }}>
      {/* Subscription header */}
      <StickyCardHeader inAside={inAside} enabled={stickyHeader}>
        {isMobile || inAside ? <Stack spacing={1.5} sx={{
        p: 2
      }}>
            <Stack direction="row" spacing={2} sx={{
          alignItems: 'center'
        }}>
              {subscriptionCheckbox}
              <ImageBox imageUrl={subscription.imageUrl ?? ''} altText={subscription.name ?? ''} sx={{
            width: 64,
            height: 64,
            flexShrink: 0
          }} />
              <Box sx={{
            flex: 1,
            minWidth: 0,
            pr: 4
          }}>
                <Typography variant="h6" component="h2" sx={theme => ({
              fontSize: pinToMinFontSize(theme.typography.h6.fontSize)
            })}>
                  {subscription.name}
                </Typography>
                <Typography variant="body3">
                  {subtitle ?? <FormattedMessage {...byDisplayType(displayTypeMessages.typeLabel, subscription.subscriptionDisplayType)} />}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} sx={{
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
              {metaRow}
              <Typography variant="body2Bold">
                <FormattedPrice amount={subscription.totalPrice ?? 0} />
              </Typography>
            </Stack>
          </Stack> : <Stack direction="row" spacing={3} sx={{
        alignItems: 'center',
        p: 3
      }}>
            {subscriptionCheckbox}
            <ImageBox imageUrl={subscription.imageUrl ?? ''} altText={subscription.name ?? ''} sx={{
          width: 80,
          height: 80,
          flexShrink: 0
        }} />
            <Box sx={{
          flex: 1,
          minWidth: 0
        }}>
              <Typography variant="h6" component="h2">
                {subscription.name}
              </Typography>
              <Typography variant="body2">
                {subtitle ?? <FormattedMessage {...byDisplayType(displayTypeMessages.typeLabel, subscription.subscriptionDisplayType)} />}
              </Typography>
              <Box sx={{
            mt: 1
          }}>{metaRow}</Box>
            </Box>
            <Typography variant="body1Bold">
              <FormattedPrice amount={subscription.totalPrice ?? 0} />
            </Typography>
            {removeButton}
          </Stack>}
        {(inAside || isMobile) && removeButton}
      </StickyCardHeader>

      {notice}

      {/* Subscription events */}
      {subscriptionEvents.map(({
      group,
      event
    }, index) => <Box key={event.id}>
          {/* A notice ends on its own edge, so the first event needs no rule
              of its own to be told apart from what is above it. */}
          {(index > 0 || !notice) && <Divider sx={{
        mx: inAside ? 2 : isMobile ? 0 : 3
      }} />}
          <Stack spacing={inAside || isMobile ? 2 : 3} sx={{
        p: inAside || isMobile ? 2 : 3
      }}>
            <Stack direction="row" spacing={1} sx={{
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
              {isSelecting && selection && !selectsWhole && <Checkbox checked={selection.ticketsState(group.tickets) === 'all'} indeterminate={selection.ticketsState(group.tickets) === 'some'} onChange={() => selection.toggleTickets(group.tickets)} slotProps={{
            input: {
              'aria-label': intl.formatMessage({
                id: "cart.selectEventItems",
                defaultMessage: [{
                  "type": 0,
                  "value": "Select all tickets for "
                }, {
                  "type": 1,
                  "value": "event"
                }]
              }, {
                event: event.name ?? ''
              })
            }
          }} sx={{
            flexShrink: 0
          }} />}
              {inAside ? <CartAsideEventCardBody eventName={event.name ?? ''} eventSubtitle={event.subTitle ?? ''} eventStartsAt={event.startDate ?? ''} eventImageUrl={event.imageUrl ?? ''} hallName={event.venue?.name ?? undefined} eventTotal={getEventTicketsTotalForGroup(group.tickets)} originalEventTotal={getEventTicketsOriginalTotalForGroup(group.tickets)} /> : <EventCardBody eventId={event.id ?? 0} eventName={event.name ?? ''} eventSubtitle={event.subTitle ?? ''} eventStartsAt={event.startDate ?? ''} eventImageUrl={event.imageUrl ?? ''} hallName={event.venue?.name ?? undefined} imageSize={isMobile ? 64 : 80} />}
            </Stack>
            <EventTicketsAccordion defaultExpanded={!inAside} tickets={group.tickets ?? []} inAside={inAside} showCustomerInfo={showCustomerInfo} currencyData={currencyData} onShow360Ticket={onShow360Ticket} onRemoveTicket={inAside || !onRemoveTicket ? undefined : ticket => onRemoveTicket(ticket as TicketDto)} onViewSeats={inAside || !onViewSeats ? undefined : () => onViewSeats(event, group.tickets ?? [])} selectedTicketIds={selectsWhole ? undefined : selection?.selectedTicketIds} onToggleTicket={isSelecting && selection && !selectsWhole ? ticket => selection.toggle(ticketKey(ticket)) : undefined} ticketSelectLabel={ticketSelectLabel} />
          </Stack>
        </Box>)}
    </Card>;
};
const getEventTicketsTotalForGroup = (tickets: TicketDto[] | null | undefined): number => (tickets ?? []).reduce((acc, ticket) => acc + (ticket.priceWithFeesAndTax ?? 0), 0);
const getEventTicketsOriginalTotalForGroup = (tickets: TicketDto[] | null | undefined): number => (tickets ?? []).reduce((acc, ticket) => acc + (ticket.originalPriceWithFeesAndTax ?? ticket.priceWithFeesAndTax ?? 0), 0);